import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import UserPicker from "../components/UserPicker";
import PpmsCatalogDialog from "./PpmsCatalogDialog";
import { ROLE_RANK, grantsOf, hasAtLeast, isGlobalAdmin, managedGroupIds, rankInGroup } from "../auth/roles";
import { useAuth } from "../auth/AuthContext";
import { PortalLayout } from "../components/Portal";
import { G, G_BG, G_DARK, Badge, StatusBadge, Field, Input, Select, Textarea, Btn, Card, Th, cellClip, formatDay, formatWhen, useSortableColumns, useColumnWidths } from "../components/Shared";
import { useDisplayTimezone, usePortalConfig } from "../portalConfig";
import { useLiveRefresh } from "../liveRefresh";
import { useDocumentWord } from "../documentWord";


const SORT_OPTIONS = [
  { value: "date_desc", label: "Newest first" },
  { value: "date_asc",  label: "Oldest first" },
  { value: "name_asc",  label: "Name A → Z" },
  { value: "name_desc", label: "Name Z → A" },
];

// ── The quotations, as one row of a list can carry them ──────────────────────
//
// **`order` in this file is the internal name; a person reads *quotation*.**
// Decided by the facility on 2026-09-16 and written down in
// knowledge/tasks/PLAN-2026-09-16-one-word-for-one-thing.md: a quotation IS the
// piece of agreed work — one quotation, one deadline, one record sent at the
// end — and `order` leaves every sentence a person reads. The `orders` table,
// `order_id`, the `/orders` routes and the `order_state` query parameter keep
// their names, because renaming a route is a contract change for one word. So
// the field names below say order, every string on screen says quotation, and
// this is the one place in the file that says so.
//
// New on 2026-09-16, with the quotations API. A project may hold thousands of
// quotations, so a row gets a SUMMARY and the full table lives on the project
// page. The four fields come from `_order_summary` in
// backend/portal/routers/projects.py, aggregated once for the whole response —
// the row must not fan out into a request per project, which is the cost
// `submission_count`/`result_count` already pay per row.
//
// **null is not zero.** The backend says so explicitly: None means "this
// response did not compute it", and four other route groups answer `ProjectOut`
// through `_project_out` without the aggregate. Painting a null as "No live
// quotation" would be the screen stating as fact that nobody is working on the
// project — so nothing is drawn at all when the count is null.
//
// **"No live quotation" is not "no quotation at all".** `live_order_count`
// counts the live ones, so 0 covers a project whose quotations are all
// finished. The question the `none` filter below asks — a project nobody has
// ever quoted — is a different one, and the two wordings are kept apart on
// purpose.

// What `order_state` may ask the server for. Mirrors ORDER_STATE_FILTERS in
// backend/portal/routers/projects.py, which refuses anything else with a 400
// rather than widening the answer in silence.
// A function of the word, because the filters are an aggregate surface and
// say the word for the current setting (frontend-screens.md, the wording rule).
const orderStateOptions = word => [
  { value: "live",    label: `Has a live ${word()}` },
  { value: "overdue", label: `Has an overdue ${word()}` },
  { value: "none",    label: `No ${word()} at all` },
];

// Which calendar day an instant falls on, as a UTC midnight that subtracts.
//
// "Today" has to be decided in the SAME zone the date is rendered in, or the
// two disagree between local midnight and the configured zone's — the trap
// `SessionLimitNotice` already paid for, where the suite failed only when it
// was run in the evening. `en-CA` because it formats as YYYY-MM-DD.
function calendarDay(value, zone) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const parts = { year: "numeric", month: "2-digit", day: "2-digit" };
  let text;
  try {
    text = new Intl.DateTimeFormat("en-CA", { ...parts, timeZone: zone || undefined }).format(d);
  } catch {
    // A zone this browser rejects falls back to the viewer's own, exactly as
    // formatWhen does — the two must agree or the comparison is meaningless.
    text = new Intl.DateTimeFormat("en-CA", parts).format(d);
  }
  const [y, m, day] = text.split("-").map(Number);
  return Date.UTC(y, m - 1, day);
}

// How the nearest deadline reads, beside the date it is counting to.
//
// **It counts CALENDAR days, from the very instant printed next to it** — not
// from `next_order_days_left`. That field is `int((deadline - now) // 86400)`
// (backend services/quotations.days_left), so it counts whole 24-hour periods
// and carries the quotation's own time of day. A quotation agreed 01 Sep at
// 14:00 with 30 days closes 01 Oct at 14:00; read at 15:00 on 30 September that
// arithmetic says 0, and the cell said "01 Oct 2026 (due today)" — the date
// saying tomorrow and the words saying today, in the same six inches of screen.
// Deadlines land at arbitrary times of day, so that was the everyday case.
// Deriving the words from the same value that is formatted beside them is what
// makes the two unable to disagree, rather than a second correction on top.
//
// **Overdue comes from the boolean, first, and never from the number.**
// `has_overdue_order` is the backend's `deadline <= now`, the same comparison
// `_assert_order_open_for_data` refuses an upload on, so a row that says
// overdue and a gate that refuses cannot disagree about the same minute. The
// quotations card and the wizard both wrote that rule down; this was the third
// copy of it and the only one that did not follow it.
//
// Past the deadline it reads as **overdue**, never as a negative number: "-3
// days" is arithmetic, not an answer. How long ago is on screen anyway, as the
// date beside it.
//
// The wording — `8 days left`, `1 day left`, `due today`, `overdue` — is the
// one the quotations card and the wizard already use for the same field, so a
// facility reading the Projects list and then the project itself sees the same
// quotation described the same way.
function deadlineCountdown(deadline, zone, overdue, now = new Date()) {
  if (!deadline) return null;
  if (overdue)   return "overdue";
  const from = calendarDay(now, zone);
  const to   = calendarDay(deadline, zone);
  if (from == null || to == null) return null;
  const days = Math.round((to - from) / 86400000);
  // Unreachable while the backend derives both from the same deadline, and
  // deliberately not arithmetic if it ever stops: the boolean decides overdue.
  if (days < 0)   return "overdue";
  if (days === 0) return "due today";
  return `${days} day${days === 1 ? "" : "s"} left`;
}

// One derivation, read by both the badge row and the tooltip beside it, so the
// summary a person sees and the one their pointer reveals cannot drift apart.
function orderSummaryParts(project, zone) {
  const count = project.live_order_count;
  if (count == null) return null;
  const overdue = project.has_overdue_order === true;
  const countdown = deadlineCountdown(project.next_order_deadline, zone, overdue);
  // A deadline the browser cannot parse empties BOTH halves at once —
  // `formatWhen` answers "" and `deadlineCountdown` answers null, off the same
  // `Number.isNaN` test — and the template interpolated that null regardless,
  // so the cell read "3 live quotations · (null)". Same decision as the null
  // count above: a value the screen could not work out is left out rather than
  // stringified into a sentence. Both halves are required, and the clause is
  // dropped whole when either is missing — half a sentence about a deadline
  // ("24 Sep 2026 ()") is no better an answer than "(null)" is.
  const day = project.next_order_deadline ? formatDay(project.next_order_deadline, zone) : null;
  return {
    count,
    overdue,
    // A quotation with no deadline is still live and still counted — the
    // backend keeps it in the population deliberately — so a count with no date
    // beside it is an ordinary state, not a missing value.
    when: day && countdown ? `${day} (${countdown})` : null,
    exact: project.next_order_deadline
      ? formatWhen(project.next_order_deadline, { zone })
      : null,
  };
}

function orderSummaryText(project, zone, word) {
  const parts = orderSummaryParts(project, zone);
  if (!parts) return "";
  return [
    parts.overdue ? "Overdue" : null,
    parts.count === 0 ? `No live ${word()}` : `${parts.count} live ${word({ plural: parts.count !== 1 })}`,
    parts.exact ? `nearest deadline ${parts.exact}` : null,
  ].filter(Boolean).join(" · ");
}

function OrderSummary({ project }) {
  const zone  = useDisplayTimezone();
  const word  = useDocumentWord();
  const parts = orderSummaryParts(project, zone);
  if (!parts) return null;
  return (
    // flexWrap is for the CARD, where there is room to wrap onto a second
    // line. In the list row it does nothing — that cell is `cellClip`, whose
    // white-space:nowrap leaves nothing to wrap — and it is kept rather than
    // moved because one component serving both views is what stops the two
    // summaries drifting apart.
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0, fontSize: 12, color: "#6b7280" }}>
      {/* The word, not only the colour: a red badge alone says nothing to a
          reader who cannot see it, and the app targets WCAG 2.2 AA. */}
      {parts.overdue && <Badge color="red">Overdue</Badge>}
      <span>
        {parts.count === 0
          ? `No live ${word()}`
          : <><strong style={{ color: "#374151" }}>{parts.count}</strong>{` live ${word({ plural: parts.count !== 1 })}`}</>}
      </span>
      {parts.when && <span>· {parts.when}</span>}
    </span>
  );
}

// ── What a project has earned, as one row and one card can carry it ──────────
//
// `money_currencies` comes from `services/stats.order_money`, aggregated ONCE
// for the whole response exactly as the quotation summary above is: four
// figures per project per currency, counted per quotation. `_project_row` runs
// per project, so pricing a row inside it would be two more queries per row on
// the one list that has no `limit`, no `offset` and ends in `.all()`.
//
// **Nothing here adds anything up.** Every figure is the string the API sent.
// `services/quotations._money_str` sends Decimals out as strings precisely so
// that no screen does arithmetic on them — 0.10 and 0.20 make
// 0.30000000000000004 in JavaScript — and a total worked out on this page could
// disagree with the PDF in the customer's hand by a cent for ever.
//
// **One block per currency, never one number over both.** The currency is
// stamped per quotation from the group and a project can be moved between
// groups, so a project holding two is legitimate; a single sum would be a
// figure in no currency at all. `/api/stats` still does that and it is filed as
// its own defect — do not copy it here.
//
// **null is not an empty list.** `null` means this response did not work the
// money out, which four route groups answering `ProjectOut` do; `[]` means the
// project has earned nothing yet, which most projects have. The first is drawn
// as nothing at all and the second as the table's own dash — painting the first
// as a dash would be the screen stating a fact it was never told, the same rule
// the quotation summary above keeps for a null count.
//
// One component for both views, for the reason `OrderSummary` is one: two
// renderings of the same block drift, and the drift is invisible because both
// of them produce a plausible number.

// The figure a narrow cell has room for is the TOTAL — outstanding plus
// delivered, lapsed deliberately not in it. The other three are named in full in
// the tooltip, which is what carries the summary whole, the same division the
// quotations cell already makes.
function moneyFigureText(figure, currency) {
  if (!figure) return null;
  // An absent gross is a real answer and never a zero: an affiliation with no
  // VAT rate has no gross at all, and nine of thirteen on dev are in that
  // state. Printed as 0.00 it would say the work was free.
  const vat = figure.gross == null ? "no VAT rate" : `${figure.gross} including VAT`;
  return `${figure.net} ${currency} (${vat})`;
}

function projectMoneyText(project, word) {
  const currencies = project.money_currencies;
  if (!currencies) return "";
  const unpriced = project.money_unpriced_orders || 0;
  const gap = unpriced > 0
    ? `${unpriced} ${word({ plural: unpriced !== 1 })} could not be priced and ${unpriced === 1 ? "is" : "are"} in none of these figures`
    : null;
  if (currencies.length === 0) {
    return [`Nothing priced yet — no ${word()} on this project has been sent or accepted`, gap]
      .filter(Boolean).join(" · ");
  }
  return currencies.map(c => [
    `Total ${moneyFigureText(c.total, c.currency)}`,
    `Delivered ${c.delivered?.net}`,
    `Outstanding ${c.outstanding?.net}`,
    `Lapsed ${c.lapsed?.net}`,
    gap,
  ].filter(Boolean).join(" · ")).join(" ; ");
}

function ProjectMoney({ project }) {
  const currencies = project.money_currencies;
  if (!currencies) return null;
  if (currencies.length === 0) return <span style={{ color: "#6b7280" }}>—</span>;
  // Quotations whose frozen figures could not be read are in none of the
  // numbers above. Marked on the face of the component rather than only in the
  // row's tooltip, because the CARD has no tooltip and a total that is quietly
  // short looks complete. The sentence in full is in `projectMoneyText`.
  const unpriced = project.money_unpriced_orders || 0;
  return (
    // flexWrap is for the CARD, where there is room for a second line; in the
    // list row the cell is `cellClip`, whose white-space:nowrap leaves nothing
    // to wrap, and the tooltip carries what is clipped. Same arrangement, and
    // for the same reason, as `OrderSummary` above.
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0, fontSize: 12, color: "#6b7280" }}>
      {currencies.map(c => (
        <span key={c.currency} data-money-currency={c.currency}
              style={{ display: "inline-flex", alignItems: "baseline", gap: 4 }}>
          <strong style={{ color: "#374151", fontFamily: "'JetBrains Mono', monospace" }}>{c.total?.net}</strong>
          <span>{c.currency}</span>
          {c.total?.gross == null
            ? <span style={{ fontStyle: "italic" }}>· no VAT rate</span>
            : <span>· {c.total.gross} incl. VAT</span>}
        </span>
      ))}
      {unpriced > 0 && (
        <span style={{ fontStyle: "italic" }}>
          · {unpriced} not priced
        </span>
      )}
    </span>
  );
}

// ── Project card (grid view) ──────────────────────────────────────────────────

function ProjectCard({ project, onClick, canManage }) {
  const zone = useDisplayTimezone();
  return (
    <div
      onClick={onClick}
      style={{
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
        padding: "22px 24px", cursor: "pointer",
        transition: "box-shadow 0.15s, border-color 0.15s",
        display: "flex", flexDirection: "column", gap: 12,
        // A grid item, and min-width auto would let one long address in the
        // card set the width of the whole column.
        minWidth: 0,
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"; e.currentTarget.style.borderColor = "#c8d5c8"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "#e5e7eb"; }}
    >
      {/* Third instance of the same mechanism as the log row and the fixed
          table columns: a flex item is min-width:auto, a project name like
          Farolfi_Versuchswirtschaft_Stromata has no spaces to break at, so the
          title refuses to shrink and pushes the badge clean out of the card —
          measured 67px past a 360px viewport. The badge must not absorb it
          either, hence flexShrink on it. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.3, minWidth: 0, overflowWrap: "anywhere" }}>{project.name}</h3>
        <div style={{ flexShrink: 0 }}><StatusBadge status={project.status} /></div>
      </div>

      {(project.representative_name || project.representative_email) && (
        <div style={{ fontSize: 13, color: "#6b7280" }}>
          {project.representative_name && <span>{project.representative_name}</span>}
          {project.representative_name && project.representative_email && <span style={{ margin: "0 6px" }}>·</span>}
          {project.representative_email && <span data-break style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{project.representative_email}</span>}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {/* Whatever this project's own facility decided to label it with. */}
        {(project.tags || []).map(t => (
          <Badge key={t.id} color={t.colour || "blue"}>{t.name}</Badge>
        ))}
      </div>

      {/* The quotations get their own line rather than a place in the counts
          row below: "3 live quotations · 24 Sep 2026 (8 days left)" is a
          sentence, and wedged between two bare numbers it read as a third
          number. */}
      <OrderSummary project={project} />

      {/* Directly under the quotations it prices, which is the chain's own
          order. Behind `canManage` — the money is the one thing on this page
          that must not cross a group line, and every card that shows money
          today sits behind the same gate. */}
      {canManage && <ProjectMoney project={project} />}

      <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 10, display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          <strong style={{ color: "#374151" }}>{project.submission_count}</strong> submission{project.submission_count !== 1 ? "s" : ""}
        </span>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          <strong style={{ color: "#374151" }}>{project.result_count}</strong> result{project.result_count !== 1 ? "s" : ""}
        </span>
        {project.group_name && (
          <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 9, background: G_BG, color: "#006630", fontWeight: 500 }}>
            {project.group_name}
          </span>
        )}
        {/* **The owner, not a group contact** (2026-09-16). This rendered
            `group_operator_*` — "any active operator or group admin in this
            project's group", taken with `.first()` and no `ORDER BY` — so it put
            a real `mailto:` on the card for somebody with no relationship to the
            project, and on PostgreSQL the name could change after a vacuum with
            nothing about the project having changed. Reported from the sandbox,
            where a project owned by one account named another. */}
        {project.owner_email && (
          <a
            data-break
            href={`mailto:${project.owner_email}`}
            onClick={e => e.stopPropagation()}
            title={`Owner: ${project.owner_email}`}
            style={{ fontSize: 11, color: "#6b7280", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 3 }}
          >
            👤 {project.owner_name || project.owner_email}
          </a>
        )}
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#c4c9d4" }}>
          {formatDay(project.created_at, zone)}
        </span>
      </div>
    </div>
  );
}

// ── Project row (list view) ───────────────────────────────────────────────────

function ProjectRow({ project, onClick, canManage, showMoney }) {
  const zone = useDisplayTimezone();
  const word = useDocumentWord();
  // cellClip because the table is tableLayout:"fixed" and Name sits directly
  // before Status: without it a name wider than its column was painted straight
  // over the status badge.
  const cell = { padding: "10px 14px", fontSize: 13, color: "#374151", verticalAlign: "middle", ...cellClip };
  return (
    <tr
      onClick={onClick}
      style={{ cursor: "pointer", borderBottom: "1px solid #f3f4f6", transition: "background 0.1s" }}
      onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      <td data-label="Name" style={{ ...cell, fontWeight: 600, color: "#1a1a1a" }} title={project.name}>{project.name}</td>
      <td data-label="Status" style={cell}><StatusBadge status={project.status} /></td>
      <td data-label="Group" style={cell}>
        {project.group_name
          ? <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 9, background: G_BG, color: "#006630", fontWeight: 500 }}>{project.group_name}</span>
          : <span style={{ color: "#6b7280" }}>—</span>}
      </td>
      <td data-label="Representative" style={{ ...cell, color: "#6b7280" }}
          title={[project.representative_name, project.representative_email].filter(Boolean).join(" · ") || undefined}>
        {project.representative_name || project.representative_email
          ? <>{project.representative_name}{project.representative_name && project.representative_email && " · "}<span data-break style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>{project.representative_email}</span></>
          : <span style={{ color: "#6b7280" }}>—</span>}
      </td>
      {/* Same correction as the chip above, and the column is renamed with it:
          it said Operator and showed a group contact. There is no per-project
          operator in the schema and this deliberately does not add one. */}
      <td data-label="Owner" style={{ ...cell, color: "#6b7280" }} title={project.owner_email || undefined}>
        {project.owner_email
          ? <a href={`mailto:${project.owner_email}`} onClick={e => e.stopPropagation()} style={{ color: G_DARK, textDecoration: "none" }}>{project.owner_name || project.owner_email}</a>
          : <span style={{ color: "#6b7280" }}>—</span>}
      </td>
      <td data-label="Subs" style={{ ...cell, textAlign: "center" }}>{project.submission_count}</td>
      <td data-label="Results" style={{ ...cell, textAlign: "center" }}>{project.result_count}</td>
      {/* `cell` clips, so the summary is cut off at the column edge rather than
          painted over Owner. Cut off, **not** ellipsised: `cellClip`'s
          text-overflow applies to overflowing inline content, and this cell's
          only child is the inline-flex span in OrderSummary — one atomic
          inline-level box, which the browser simply clips mid-glyph. The title
          is what carries the summary whole, to the minute, so it is the fix
          rather than a nicety. undefined and not "" — an empty tooltip is
          announced as one. */}
      <td data-label={word({ plural: true, capital: true })} style={cell} title={orderSummaryText(project, zone, word) || undefined}>
        <OrderSummary project={project} />
      </td>
      {/* Beside the quotations whose work it prices. `showMoney` is the page's
          decision and `canManage` this project's: the column exists when the
          account manages something on the page, and each cell answers for its
          own project — the list mixes groups, and an operator of one group is
          an ordinary member of another group's project. A cell it may not fill
          is left empty rather than dashed: a dash would read as "nothing". */}
      {showMoney && (
        <td data-label="Money" style={cell}
            title={(canManage && projectMoneyText(project, word)) || undefined}>
          {canManage && <ProjectMoney project={project} />}
        </td>
      )}
      <td data-label="Created" style={{ ...cell, color: "#6b7280", fontSize: 11, whiteSpace: "nowrap" }}>
        {formatDay(project.created_at, zone)}
      </td>
    </tr>
  );
}

// ── New project form ──────────────────────────────────────────────────────────

function NewProjectForm({ onCreated, onCancel, isAdmin, myGroups }) {
  // Data Type and Organism are gone from the form. They are ordinary group
  // fields now, so a facility that wants them defines them and every other one
  // is not asked.
  // The representative is an account, not a name somebody typed: the form sends
  // the id and the server derives the name and the address from that account.
  // The label is local and only so the picker can show who is chosen.
  const [form, setForm] = useState({ name: "", representative_user_id: null, notes: "",
                                     group_id: "", affiliation_id: "" });
  const [repLabel, setRepLabel] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy]   = useState(false);
  const [groups, setGroups] = useState([]);
  const [affiliations, setAffiliations] = useState([]);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const word = useDocumentWord();

  // An admin picks from every group and has to ask the server for the list.
  // Everybody else picks from the groups they manage, which their own grants
  // already name — /api/admin/groups is admin-only and would 403 for them.
  useEffect(() => {
    if (!isAdmin) { setGroups(myGroups || []); return; }
    api.get("/api/admin/groups").then(setGroups).catch(() => {});
  }, [isAdmin, myGroups]);

  // Which affiliations this group prices. The choices come from the imported
  // PPMS export, not from a list kept here — PPMS decides what kinds of
  // customer exist, and they belong to the group whose import created them.
  // Until 2026-08-24 an unpriced group fell back to an institution-wide list
  // server-side; group-scoping killed that fallback, because what it fell back
  // to was other facilities' affiliations — it was handing one group's customer
  // categories to another. list_affiliations answers [] now, so an empty picker
  // is the ordinary state of a group that has not imported its own price list
  // yet, not a sign that nothing has ever been imported anywhere. The submit
  // below lets that case through and the server refuses at project creation,
  // which is where somebody can act on it: import the price list.
  const chosenGroup = form.group_id || (groups.length === 1 ? groups[0].id : null);
  useEffect(() => {
    if (!chosenGroup) { setAffiliations([]); return; }
    api.get(`/api/affiliations?group_id=${chosenGroup}`)
      .then(setAffiliations).catch(() => setAffiliations([]));
  }, [chosenGroup]);

  const handleSubmit = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // "" is not a number and would 422; a project with no group is a real
      // state, so the field is left out rather than sent empty.
      const payload = { ...form };
      if (payload.group_id === "") delete payload.group_id;
      else payload.group_id = Number(payload.group_id);
      // Not optional either, and checked first because the server checks it
      // first — with both missing, the message on screen and the one the
      // server would have sent name the same field.
      if (payload.representative_user_id == null) {
        setError(`Choose a project representative — they are who a ${word()} is sent to, `
                 + "and they have to have an account here.");
        setBusy(false);
        return;
      }
      // Not optional. Refused here rather than sent empty for the server to
      // reject, so the message names the field instead of arriving as a 400
      // about a payload.
      if (payload.affiliation_id === "" || payload.affiliation_id == null) {
        if (affiliations.length > 0) {
          setError("Choose an affiliation — it decides which price column applies.");
          setBusy(false);
          return;
        }
        delete payload.affiliation_id;
      } else {
        payload.affiliation_id = Number(payload.affiliation_id);
      }
      const project = await api.post("/api/projects", payload);
      onCreated(project);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ marginBottom: 28 }}>
      <h2 style={{ margin: "0 0 22px", fontSize: 17, fontWeight: 700, paddingBottom: 14, borderBottom: `2px solid ${G}` }}>
        New Project
      </h2>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div data-form-grid style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div style={{ gridColumn: "1/-1" }}>
            <Field label="Project Name" required>
              <Input placeholder="e.g. Smith_Lab_2026" value={form.name} onChange={set("name")} />
            </Field>
          </div>
          {/* "the billing record", not "the invoicing record": the portal does
              not invoice. It sends the signed quotations and whether they
              finished, and somebody else writes the invoice from that — the
              same decision behind the field called *Invoice recipients* until 2026-09-16,
              which is *Billing recipients* now. The old name and the date share
              one line on purpose: `test_billing_recipients_name.py` exempts a
              note carrying that exact date and nothing else, and it reads line
              by line — so a dated paragraph does not make its other lines safe. */}
          <Field label="Project representative" required
                 hint={`Start typing a name. It has to be somebody with an account here — they are who a ${word()} is sent to and who the billing record names.`}>
            <UserPicker
              value={form.representative_user_id}
              valueLabel={repLabel}
              onChange={person => {
                // Only the id is sent. The name and the address on the project
                // are the account's, derived server-side, so there is nowhere
                // left for the two to drift apart.
                setRepLabel(person ? (person.full_name || person.email) : "");
                setForm(f => ({ ...f, representative_user_id: person ? person.id : null }));
              }}
            />
          </Field>
          {/* Shown when there is a choice to make. Somebody who manages exactly
              one group has none — the server files it there — but somebody who
              manages two must say which, or the server refuses with nothing on
              screen to fix it. */}
          {(isAdmin || groups.length > 1) && (
            <Field label="Group" hint="Which operators can see this project. Without one it is visible only to admins and its owner, and can only be handed to an admin.">
              <Select options={groups.map(g => ({ value: g.id, label: g.name }))} value={form.group_id} onChange={set("group_id")} />
            </Field>
          )}
          {affiliations.length > 0 && (
            <Field label="Affiliation" required
                   hint="Which price column applies to this customer. It can be changed later from the project's details.">
              {/* An explicit empty first option. Without it the browser shows
                  the first affiliation as though it were chosen while the form
                  still holds "", so somebody would submit a price category they
                  never looked at. */}
              <Select options={[{ value: "", label: "— choose one —" },
                                ...affiliations.map(a => ({ value: String(a.id), label: a.label || a.code }))]}
                      value={String(form.affiliation_id ?? "")}
                      onChange={set("affiliation_id")} />
            </Field>
          )}
          <div style={{ gridColumn: "1/-1" }}>
            <Field label="Notes">
              <Textarea placeholder="Project description, special instructions…" value={form.notes} onChange={set("notes")} />
            </Field>
          </div>
        </div>

        {error && (
          <div role="alert" style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
          <Btn type="submit" disabled={busy || !form.name}>{busy ? "Creating…" : "Create Project"}</Btn>
        </div>
      </form>
    </Card>
  );
}

// ── View toggle button ────────────────────────────────────────────────────────

function ViewBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 10px", border: "1px solid #e5e7eb", background: active ? G_BG : "#fff",
        color: active ? G_DARK : "#6b7280", fontWeight: active ? 600 : 400,
        borderRadius: 7, cursor: "pointer", fontSize: 13, lineHeight: 1,
        fontFamily: "inherit", transition: "all 0.15s",
      }}
    >
      {children}
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ProjectsPage() {
  const navigate              = useNavigate();
  const { user }              = useAuth();
  // Projects are set up by the facility, not by the people submitting to them —
  // the API refuses it either way, this just stops offering a dead button.
  //
  // hasAtLeast, not `role === "operator"`: `user.role` is a cache of the
  // STRONGEST grant, so an account holding operator AND group admin reads as
  // "group_admin" and failed this test — the button vanished for exactly the
  // people who run the facility.
  const isStaff               = hasAtLeast(user, "operator");
  // **And only while projects may be created by hand at all** (2026-10-14).
  // With `manual_project_creation` off the server refuses everyone, admins
  // included, so the button would be a dead one for every role. `!== false`,
  // not truthiness: a server that does not send the key yet has the old
  // behaviour, and the fallback before the config arrives is on.
  const { manual_project_creation: manualCreation, ppms_enabled: ppmsEnabled } = usePortalConfig();
  const fromPpmsOnly          = manualCreation === false;
  const canCreate             = isStaff && !fromPpmsOnly;
  // The other way in (PLAN-2026-10-20-the-ppms-catalogue.md): any PPMS project,
  // found by name in the catalogue. Offered whenever PPMS is on, whatever the
  // creation switch says — the server gates the import on the group, not on it.
  const canImport             = isStaff && ppmsEnabled === true;
  const [showImport, setShowImport] = useState(false);
  const zone                  = useDisplayTimezone();
  const word                  = useDocumentWord();
  // Groups this person may file a project into, from their own grants — which
  // already carry the name, so no admin-only lookup is needed.
  const myGroups              = grantsOf(user)
    .filter(g => g.group_id != null && managedGroupIds(user).includes(g.group_id))
    .map(g => ({ id: g.group_id, name: g.group_name || `Group ${g.group_id}` }))
    .filter((g, i, all) => all.findIndex(o => o.id === g.id) === i);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showNew, setShowNew]   = useState(false);
  const [error, setError]       = useState(null);

  const [view,         setView]         = useState(() => localStorage.getItem("projectsView") || "grid");
  const [search,       setSearch]       = useState("");
  const [filterTag,    setFilterTag]    = useState("");
  const [filterFrom,   setFilterFrom]   = useState("");
  const [filterTo,     setFilterTo]     = useState("");
  const [filterGroup,  setFilterGroup]  = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  // By the project's QUOTATIONS, and answered by the server — see `load` below.
  const [filterOrders, setFilterOrders] = useState("");   // "" | live | overdue | none
  const [filterDueBy,  setFilterDueBy]  = useState("");   // order_due_before, a date
  const [sortBy,       setSortBy]       = useState("date_desc");

  // `quiet` for a re-read in the background: the list stays on screen rather
  // than giving way to the spinner every time the tab comes back into view.
  const load = ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (filterTag)  params.set("tag_id", filterTag);
    if (filterFrom) params.set("created_from", filterFrom);
    if (filterTo)   params.set("created_to", filterTo);
    // The quotation filters go to the server for the same reason the text does,
    // and
    // for a second one. The browser holds only the projects this person may
    // see, and only a summary of each project's quotations; the server answers both
    // filters from the one aggregate it already computes for the response. And
    // a filter must never be able to WIDEN what somebody may see — that is a
    // property of the server's query, not of what a browser does with rows it
    // has already been handed. Filtering the rendered rows here would answer a
    // different question and look identical doing it.
    if (filterOrders) params.set("order_state", filterOrders);
    if (filterDueBy)  params.set("order_due_before", filterDueBy);
    const qs = params.toString();
    api.get(qs ? `/api/projects?${qs}` : "/api/projects")
      .then(data => { setProjects(data); setError(null); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  // Debounced, so typing does not put a query on the database per keystroke.
  useEffect(() => {
    const timer = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterTag, filterFrom, filterTo, filterOrders, filterDueBy]);

  // The counts on each card — submissions, results, what waits for a
  // signature — change on other pages and in other people's sessions; this
  // re-reads them, with the filters as they are now. See liveRefresh.js.
  useLiveRefresh(() => load({ quiet: true }));

  const handleCreated = project => {
    setShowNew(false);
    navigate(`/projects/${project.id}`);
  };

  const switchView = v => { setView(v); localStorage.setItem("projectsView", v); };

  // One function, called from both "Clear filters" buttons. They were two
  // identical inline closures, which is how a new filter comes to be cleared by
  // one of them and not the other.
  const clearFilters = () => {
    setSearch(""); setFilterTag(""); setFilterFrom(""); setFilterTo("");
    setFilterGroup(""); setFilterStatus(""); setFilterOrders(""); setFilterDueBy("");
  };

  const groupOptions  = useMemo(() => [...new Set(projects.map(p => p.group_name).filter(Boolean))].sort(), [projects]);
  const tagOptions    = useMemo(() => {
    const seen = new Map();
    projects.forEach(p => (p.tags || []).forEach(t => seen.set(t.id, t)));
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);
  const statusOptions = useMemo(() => [...new Set(projects.map(p => p.status).filter(Boolean))].sort(), [projects]);

  // Text, tags and dates go to the SERVER, and the browser only ever holds the
  // projects this person may see. Filtering a partial copy here would quietly
  // answer "no matches" for a project that exists — which is worse than a
  // slower search. That is the reason, and it still holds; what the comment
  // said about the reach did not. `search` reads FOUR columns and joins no
  // membership: name, representative_name, representative_email and notes
  // (`projects.py` `list_projects`). The placeholder below says exactly that
  // and stays as it is. The one parameter that would reach members is
  // `member_email` — it exists on the API, joins User.email and also matches
  // the representative's address as text, and NO screen sends it (corrected
  // 2026-09-16, when the comment still claimed members). Deliberately not
  // widened: folding a membership join into `search` would silently change
  // what every existing caller of that parameter gets back.
  const filtered = useMemo(() => {
    return projects
      .filter(p => hasAtLeast(user, "operator") ? true : p.status !== "archived")
      .filter(p => !filterGroup  || p.group_name === filterGroup)
      .filter(p => !filterStatus || p.status     === filterStatus)
      .sort((a, b) => {
        if (sortBy === "name_asc")  return a.name.localeCompare(b.name);
        if (sortBy === "name_desc") return b.name.localeCompare(a.name);
        if (sortBy === "date_asc")  return new Date(a.created_at) - new Date(b.created_at);
        return new Date(b.created_at) - new Date(a.created_at);
      });
  }, [projects, user, filterGroup, filterStatus, sortBy]);

  // `showOwner` went on 2026-09-16 with the column it gated. It hid an
  // admin-only "Owner" column showing `@created_by_username` — which, once the
  // Operator column was corrected to name the owner, was the same person twice
  // in one row, one of them as a login name. The correction made it redundant
  // rather than wrong.
  const hasFilters = search || filterTag || filterFrom || filterTo || filterGroup
    || filterStatus || filterOrders || filterDueBy;

  const projectAccessors = useMemo(() => ({
    name:     p => p.name,
    status:   p => p.status || "",
    group:    p => p.group_name || "",
    representative: p => p.representative_name || p.representative_email || "",
    owner: p => p.owner_name || p.owner_email || "",
    subs:     p => p.submission_count,
    results:  p => p.result_count,
    // By urgency: the nearest deadline first, sorted on the very instant the
    // cell prints, so the order of the rows and the words in them cannot
    // disagree. Not `next_order_days_left` — it floors to whole 24-hour
    // periods, so two quotations closing eleven hours apart tie.
    //
    // A project the response did not summarise, or one whose live quotations
    // carry no deadline, has no position in that order. `useSortableColumns`
    // puts a null LAST and then reverses the whole array for descending, so the
    // second click on this header leads with them — the rows with nothing to
    // say above the most overdue ones. Pinned in the test in both directions
    // rather than worked around here: the reversal is Shared.jsx's and every
    // sortable table in the app shares it, so a sentinel here would fix one
    // column and leave the shape.
    orders:   p => {
      const t = p.next_order_deadline ? new Date(p.next_order_deadline).getTime() : NaN;
      return Number.isNaN(t) ? null : t;
    },
    // What the project has earned and still expects, sorted on the ONE figure
    // the cell prints: the total, of its single currency.
    //
    // A project holding two currencies has no position in this order, and that
    // is the decision rather than a gap: 1 000 CZK against 300 EUR is the same
    // comparison across currencies the cell itself refuses to make, one pair at
    // a time. Nor does one uncosted by this response. `useSortableColumns` puts
    // a null LAST and then reverses the whole array for descending, so the
    // second click leads with the rows that have nothing to say — Shared.jsx's
    // rule, shared by every sortable table here, pinned in the test rather than
    // worked around in this one accessor.
    //
    // `Number` here is a COMPARISON and never a sum: the strings exist so that
    // no screen adds them up, and ordering them adds nothing up.
    money:    p => {
      const currencies = p.money_currencies;
      if (!currencies || currencies.length !== 1) return null;
      const net = Number(currencies[0].total?.net);
      return Number.isFinite(net) ? net : null;
    },
    created:  p => new Date(p.created_at).getTime(),
  }), []);
  const { sorted: listSorted, sortKey: listSortKey, sortDir: listSortDir, toggleSort: toggleListSort } = useSortableColumns(filtered, projectAccessors);
  const { widths: listWidths, setWidth: setListWidth, totalWidth: listTotalWidth } = useColumnWidths({
    name: 170, status: 110, group: 140, representative: 200, tags: 160, operator: 170, subs: 80, results: 80, orders: 190, money: 180, owner: 120, created: 110,
  });

  // Standing in THIS project's group, mirroring the backend's `_manages_project`
  // and `ProjectDetailPage`'s own `canManage` — not `hasAtLeast`, which answers
  // "operator somewhere" and would show one group's money on another group's
  // project. The money is deliberately narrower than the server allows: the
  // same call the card-scoped export made, and widening it to members is the
  // facility's decision and its own task.
  const canManageProject = p =>
    isGlobalAdmin(user)
    || (rankInGroup(user, p.group_id) !== null
        && rankInGroup(user, p.group_id) <= ROLE_RANK.operator);
  // Whether the column exists at all, decided over everything loaded rather
  // than over `filtered`, so that changing a filter cannot make a column appear
  // and disappear under the pointer. An account that manages nothing gets no
  // column: an always-blank one reads as "these projects have earned nothing",
  // which is a statement, and a wrong one.
  const showMoney = projects.some(canManageProject);

  return (
    <PortalLayout>
      <div data-page-header style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Projects</h1>
          <p style={{ margin: "4px 0 0", fontSize: 14, color: "#6b7280" }}>
            {/* An operator sees only what they own or are assigned to since
                2026-11-03; the whole group is a group admin's view. */}
            {isGlobalAdmin(user) ? "All projects"
              : hasAtLeast(user, "group_admin") ? "Your group's projects"
              : hasAtLeast(user, "operator") ? "Projects you own or are assigned to"
              : "Your projects"}
          </p>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <div data-page-actions style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {canImport && <Btn variant="secondary" onClick={() => setShowImport(true)}>Import from PPMS</Btn>}
            {!showNew && canCreate && <Btn onClick={() => setShowNew(true)}>+ New Project</Btn>}
          </div>
          {isStaff && fromPpmsOnly && (
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280", maxWidth: 320, textAlign: "right" }}>
              New projects come from PPMS on this portal
              {canImport ? <>: find one with <em>Import from PPMS</em>.</> : "."}
            </p>
          )}
        </div>
      </div>

      {showImport && (
        <PpmsCatalogDialog zone={zone} onClose={() => setShowImport(false)}
                           onImported={project => { setShowImport(false); navigate(`/projects/${project.id}`); }} />
      )}

      {showNew && (
        <NewProjectForm onCreated={handleCreated} onCancel={() => setShowNew(false)}
                          isAdmin={isGlobalAdmin(user)} myGroups={myGroups} />
      )}

      {error && (
        <div role="alert" style={{ padding: "12px 16px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13, marginBottom: 20 }}>
          {error}
        </div>
      )}

      {/* Toolbar */}
      {/* Kept mounted whatever the results: a search that finds nothing must
          still be editable, and hiding the toolbar on an empty result is how a
          filter becomes impossible to undo. */}
      {!loading && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 16, padding: "12px 16px", background: "#f9fafb", borderRadius: 12, border: "1px solid #e5e7eb" }}>
          <input
            type="search"
            placeholder="Search name, representative, notes…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: "1 1 160px", minWidth: 140, padding: "6px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit", outline: "none" }}
          />
          {tagOptions.length > 0 && (
            <select value={filterTag} onChange={e => setFilterTag(e.target.value)}
              aria-label="Filter by tag"
              style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit", background: filterTag ? G_BG : "#fff", color: filterTag ? G_DARK : "#374151", cursor: "pointer" }}>
              <option value="">All tags</option>
              {tagOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          {/* data-field-row: on a phone every input is width 100%, and two
              dates in one row that cannot wrap made /projects 388px wide at
              360 (scripts/mobile-review, 2026-09-30). */}
          <label data-field-row style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#6b7280" }}>
            Created
            <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)}
              aria-label="Created from"
              style={{ padding: "5px 8px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit" }} />
            to
            <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)}
              aria-label="Created to"
              style={{ padding: "5px 8px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit" }} />
          </label>
          {/* Always offered, unlike the tag and group pickers beside it, which
              are built from what is currently on screen. These ask the server a
              question about rows it has NOT sent — "no quotation at all" most of
              all — so gating them on the current results would hide the filter
              exactly when it has something to say. */}
          <select value={filterOrders} onChange={e => setFilterOrders(e.target.value)}
            aria-label={`Filter by ${word({ plural: true })}`}
            style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit", background: filterOrders ? G_BG : "#fff", color: filterOrders ? G_DARK : "#374151", cursor: "pointer" }}>
            <option value="">Any {word({ plural: true })}</option>
            {orderStateOptions(word).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <label data-field-row style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#6b7280" }}>
            {word({ capital: true })} due before
            {/* Exclusive, and the server says so: the day named is the first one
                OUTSIDE the answer, unlike the Created range above, which reads
                "up to and including". */}
            <input type="date" value={filterDueBy} onChange={e => setFilterDueBy(e.target.value)}
              aria-label={`${word({ capital: true })} due before`}
              style={{ padding: "5px 8px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit" }} />
          </label>
          {groupOptions.length > 0 && (
            <select value={filterGroup} onChange={e => setFilterGroup(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit", background: filterGroup ? G_BG : "#fff", color: filterGroup ? G_DARK : "#374151", cursor: "pointer" }}>
              <option value="">All groups</option>
              {groupOptions.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          )}
          {statusOptions.length > 1 && (
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit", background: filterStatus ? G_BG : "#fff", color: filterStatus ? G_DARK : "#374151", cursor: "pointer" }}>
              <option value="">All statuses</option>
              {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 13, fontFamily: "inherit", background: "#fff", cursor: "pointer" }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {hasFilters && (
            <button onClick={clearFilters}
              style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid #fca5a5", background: "rgba(220,38,38,0.05)", color: "#b91c1c", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
              Clear filters
            </button>
          )}
          <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            <ViewBtn active={view === "grid"} onClick={() => switchView("grid")}>⊞ Grid</ViewBtn>
            <ViewBtn active={view === "list"} onClick={() => switchView("list")}>☰ List</ViewBtn>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280" }}>Loading projects…</div>
      ) : projects.length === 0 && !hasFilters ? (
        <div style={{ textAlign: "center", padding: "80px 24px" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: G_BG, border: `2px solid rgba(0,150,64,0.2)`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", fontSize: 28 }}>
            📁
          </div>
          <h3 style={{ margin: "0 0 8px", color: "#374151" }}>No projects yet</h3>
          {isStaff && fromPpmsOnly ? (
            /* Where projects come from, rather than an empty page with no way
               forward: the catalogue is the only way in while creation is off.
               Until 2026-10-20 this named the per-account *Sync with PPMS*,
               which left with the account's PPMS login. */
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 20px" }}>
              Projects on this portal are created from PPMS: the server keeps a catalogue of
              every PPMS project{canImport
                ? <>, and <em>Import from PPMS</em> above finds one by name and creates it in a group
                    you manage. Who represents a project can be named then or set on it afterwards.</>
                : <>, but PPMS is not configured on this portal — an administrator has to set it up first.</>}
            </p>
          ) : (
            <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 20px" }}>Create your first project to start submitting data.</p>
          )}
          {canCreate && <Btn onClick={() => setShowNew(true)}>+ New Project</Btn>}
        </div>
      ) : filtered.length === 0 ? (
        /* Reached with filters set and nothing matching — including the case
           where the SERVER returned nothing. Before the quotation filters, an empty
           server answer fell into the branch above and told somebody who had
           just searched that they had no projects and should create their
           first. `order_state=none` makes that the ordinary outcome. */
        <div style={{ textAlign: "center", padding: "60px 0", color: "#6b7280" }}>
          <div style={{ fontSize: 24, marginBottom: 12 }}>🔍</div>
          No projects match the current filters.{" "}
          <button onClick={clearFilters}
            style={{ background: "none", border: "none", color: G_DARK, cursor: "pointer", textDecoration: "underline", fontSize: "inherit", fontFamily: "inherit" }}>
            Clear filters
          </button>
        </div>
      ) : view === "grid" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
          {filtered.map(p => (
            <ProjectCard key={p.id} project={p} canManage={canManageProject(p)}
                         onClick={() => navigate(`/projects/${p.id}`)} />
          ))}
        </div>
      ) : (
        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="responsive-table" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: listTotalWidth - listWidths.owner - (showMoney ? 0 : listWidths.money) }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  <Th label="Name" sortKey="name" activeKey={listSortKey} dir={listSortDir} onSort={toggleListSort} width={listWidths.name} onWidth={w => setListWidth("name", w)} />
                  <Th label="Status" sortKey="status" activeKey={listSortKey} dir={listSortDir} onSort={toggleListSort} width={listWidths.status} onWidth={w => setListWidth("status", w)} />
                  <Th label="Group" sortKey="group" activeKey={listSortKey} dir={listSortDir} onSort={toggleListSort} width={listWidths.group} onWidth={w => setListWidth("group", w)} />
                  <Th label="Representative" sortKey="representative" activeKey={listSortKey} dir={listSortDir} onSort={toggleListSort} width={listWidths.representative} onWidth={w => setListWidth("representative", w)} />
                  <Th label="Owner" sortKey="owner" activeKey={listSortKey} dir={listSortDir} onSort={toggleListSort} width={listWidths.operator} onWidth={w => setListWidth("operator", w)} />
                  <Th label="Subs" align="center" sortKey="subs" activeKey={listSortKey} dir={listSortDir} onSort={toggleListSort} width={listWidths.subs} onWidth={w => setListWidth("subs", w)} />
                  <Th label="Results" align="center" sortKey="results" activeKey={listSortKey} dir={listSortDir} onSort={toggleListSort} width={listWidths.results} onWidth={w => setListWidth("results", w)} />
                  <Th label={word({ plural: true, capital: true })} sortKey="orders" activeKey={listSortKey} dir={listSortDir} onSort={toggleListSort} width={listWidths.orders} onWidth={w => setListWidth("orders", w)} />
                  {showMoney && <Th label="Money" sortKey="money" activeKey={listSortKey} dir={listSortDir} onSort={toggleListSort} width={listWidths.money} onWidth={w => setListWidth("money", w)} />}
                  <Th label="Created" sortKey="created" activeKey={listSortKey} dir={listSortDir} onSort={toggleListSort} width={listWidths.created} onWidth={w => setListWidth("created", w)} />
                </tr>
              </thead>
              <tbody>
                {listSorted.map(p => (
                  <ProjectRow key={p.id} project={p} showMoney={showMoney}
                              canManage={canManageProject(p)}
                              onClick={() => navigate(`/projects/${p.id}`)} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filtered.length > 0 && projects.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: "#6b7280", textAlign: "right" }}>
          {filtered.length === projects.length ? `${projects.length} project${projects.length !== 1 ? "s" : ""}` : `${filtered.length} of ${projects.length} projects`}
        </div>
      )}
    </PortalLayout>
  );
}
